<template>
    <v-layout v-if="ready" justify-center>
        <v-responsive max-width="1024px" width="100%" height="100%" class="shop-board-view">
            <board-view-heading v-model="board" />
            <v-card class="mt-4">
                <v-card-title class="subtitle-1 pb-0">기본정보</v-card-title>
                <v-card-text>
                    <v-row v-if="$route.params._board">
                        <v-col cols="6" class="py-0">
                            <v-text-field :value="(board.writer || {}).name" label="작성자" persistent-placeholder hide-details readonly disabled></v-text-field>
                        </v-col>
                        <v-col cols="6" class="py-0">
                            <v-datetime-field
                                :value="board.createdAt ? board.createdAt.toDateTime() : undefined"
                                label="작성일"
                                persistent-placeholder
                                hide-details
                                @input="(value) => (board.createdAt = value)"
                            ></v-datetime-field>
                        </v-col>
                    </v-row>
                    <v-row class="mt-3">
                        <v-col cols="6" class="py-0">
                            <v-text-field v-model="board.subject" label="제목" persistent-placeholder hide-details />
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <board-view-files v-model="board.files" />

            <v-card class="mt-6">
                <v-card-title class="subtitle-1">내용</v-card-title>
                <v-card-text>
                    <naver-smarteditor v-model="board.content" id="content" rows="10"></naver-smarteditor>
                </v-card-text>
            </v-card>

            <v-card v-if="['interior-inquire'].includes($route.query.code)" class="mt-6">
                <v-card-title class="subtitle-1">답변</v-card-title>
                <v-card-text>
                    <naver-smarteditor v-model="board.reply" id="reply" rows="10"></naver-smarteditor>
                </v-card-text>
            </v-card>

            <v-row justify="center">
                <v-col md="auto" sm="auto">
                    <v-btn color="primary" @click="save">저장</v-btn>
                </v-col>
            </v-row>
        </v-responsive>
    </v-layout>
</template>

<script>
import api from "@/api";

import VDatetimeField from "@/components/plugins/vuetify/v-datetime-field.vue";
import BoardViewFiles from "@/components/console/boards/board-view-files.vue";
import BoardViewHeading from "@/components/console/boards/board-view-heading.vue";
import NaverSmarteditor from "@/components/plugins/naver/naver-smarteditor.vue";

export default {
    components: {
        VDatetimeField,
        BoardViewFiles,
        BoardViewHeading,
        NaverSmarteditor,
    },
    data() {
        return {
            ready: false,
            saving: false,

            ///////////////////////////////////////////////
            // 게시글 정보
            ///////////////////////////////////////////////
            board: {
                code: this.$route.query.code,
                category: undefined,

                subject: undefined,
                content: undefined,
                files: [],
                createdAt: undefined,

                sequence: 1,

                writer: {
                    name: undefined,
                    phone: undefined,
                    email: undefined,
                },
            },
        };
    },
    mounted() {
        this.init();
    },
    methods: {
        async init() {
            try {
                if (this.$route.params._board) {
                    var { board } = await api.console.boards.get({ _id: this.$route.params._board });
                    this.board = board;

                    if (board.thumb) this.board.thumb = await api.getResource(board.thumb);
                    // 기존 첨부는 문서 그대로 둔다(칩에 이름만 보이면 된다). 파일로 다시 받아 올리지 않는다
                    if (board.files) this.board.files = board.files.map((file) => ({ ...file, name: file.fileName }));
                }

                if (!this.$route.params._board === undefined) {
                    var { summary } = await api.console.boards.gets({
                        headers: { skip: 0, limit: 1 },
                        params: {
                            code: this.$route.query.code,
                        },
                    });
                    this.board.sequence = summary.totalCount + 1;
                }

                this.ready = true;
            } catch (error) {
                console.error(error);
                if (error.response) alert(error.response.data.message);
            }
        },
        // 첨부 저장 순서: 게시글 저장 → 새 파일 올리기 → 뺀 첨부 지우기
        // 중간에 실패해도 기존 첨부는 남는다 (먼저 지우면 업로드 실패 때 첨부가 사라진다)
        // 첨부 연결(board._files)은 서버가 올릴 때 넣고 지울 때 뺀다. 한 게시글을 동시에 고치므로 하나씩 보낸다
        async save() {
            if (this.saving) return;
            else this.saving = true;

            try {
                const { _files: savedFiles = [], files = [], thumb, ...boardData } = this.board;
                const isCreate = !this.board._id;

                ////////////////////////////////////////////////////////////////
                // 1. 게시글 저장. 첨부가 실패한 뒤 다시 저장해도 글이 둘이 되지 않게 _id 를 바로 잡아 둔다
                ////////////////////////////////////////////////////////////////
                var { board } = isCreate ? await api.console.boards.post(boardData) : await api.console.boards.put(boardData);
                this.board._id = board._id;

                ////////////////////////////////////////////////////////////////
                // 2. 썸네일, 새 첨부 올리기. 올린 것은 바로 문서로 바꿔 다시 저장할 때 또 올리지 않는다
                //    폴더 이름을 매번 새로 붙여 뺀 첨부와 경로가 겹치지 않게 한다
                ////////////////////////////////////////////////////////////////
                if (thumb) await api.console.boards.postThumb(board, thumb);

                const stamp = Date.now();
                for (const [index, file] of files.entries()) {
                    if (!(file instanceof File)) continue;
                    const { file: uploaded } = await api.console.boards.files.post({ _board: board._id, index: `${stamp}-${index}` }, file);
                    this.board.files.splice(index, 1, { ...uploaded, name: uploaded.fileName });
                    this.board._files = [...(this.board._files || []), uploaded._id]; // 서버가 이미 연결했다. 다시 저장하다 빼면 지울 수 있게 적어 둔다
                }

                ////////////////////////////////////////////////////////////////
                // 3. 뺀 첨부만 지운다
                ////////////////////////////////////////////////////////////////
                const _files = this.board.files.map(({ _id }) => _id);
                for (const _id of savedFiles.filter((_id) => !_files.includes(_id))) {
                    await api.console.boards.files.delete({ _id });
                }
                this.board._files = _files;

                alert(isCreate ? "저장되었습니다" : "수정되었습니다");

                this.$router.go(-1);
            } catch (error) {
                console.error(error);
                // 403 은 axios 인터셉터가 이미 알린다 (@/plugins/axios.js)
                if (error.response?.status !== 403) alert("저장하지 못했습니다. 다시 시도해 주세요.");
            } finally {
                this.saving = false;
            }
        },

        createObjectURL(file) {
            try {
                return URL.createObjectURL(file);
            } catch (error) {
                return;
            }
        },
    },
};
</script>
